import { useEffect, useState } from "react";
import { REGIONS } from "@/lib/locations";

// Center coordinates for each region (used for distance calculation)
export const REGION_CENTERS: Record<string, { lat: number; lng: number }> = {
  "sacramento-area":  { lat: 38.5816, lng: -121.4944 },
  "contra-costa":     { lat: 37.9101, lng: -122.0652 },
  "solano":           { lat: 38.2494, lng: -122.0400 },
  "alameda":          { lat: 37.6688, lng: -122.0808 },
  "san-diego":        { lat: 32.7157, lng: -117.1611 },
  "orange-county":    { lat: 33.6846, lng: -117.8265 },
  "santa-clara":      { lat: 37.3382, lng: -121.8863 },
  "marin":            { lat: 37.9735, lng: -122.5311 },
  "san-francisco":    { lat: 37.7749, lng: -122.4194 },
  "los-angeles":      { lat: 34.0522, lng: -118.2437 },
  "phoenix-area":     { lat: 33.4484, lng: -112.0740 },
};

export function haversineDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 3958.8; // Earth radius in miles
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function findNearestRegion(lat: number, lng: number): string | null {
  let closest: string | null = null;
  let minDist = Infinity;

  for (const [slug, center] of Object.entries(REGION_CENTERS)) {
    const dist = haversineDistance(lat, lng, center.lat, center.lng);
    if (dist < minDist) {
      minDist = dist;
      closest = slug;
    }
  }

  // Only suggest a region if the user is within 60 miles of its center
  return minDist <= 60 ? closest : null;
}

export type GeoDetectionResult = {
  status: "idle" | "detecting" | "detected" | "dismissed" | "outside";
  detectedRegion: string | null;
  detectedRegionName: string | null;
  detectedCity: string | null;
  dismiss: () => void;
};

const SESSION_KEY = "uplevel_geo_dismissed";

export function useGeoLocation(): GeoDetectionResult {
  const [status, setStatus] = useState<GeoDetectionResult["status"]>("idle");
  const [detectedRegion, setDetectedRegion] = useState<string | null>(null);
  const [detectedCity, setDetectedCity] = useState<string | null>(null);

  const dismiss = () => {
    sessionStorage.setItem(SESSION_KEY, "1");
    localStorage.setItem(SESSION_KEY, "1"); // Use localStorage for stronger persistence
    setStatus("dismissed");
  };

  useEffect(() => {
    // Only run once per session (or forever if in localStorage)
    if (sessionStorage.getItem(SESSION_KEY) || localStorage.getItem(SESSION_KEY)) {
      setStatus("dismissed");
      return;
    }

    const CACHE_KEY = "uplevel_geo_cache";
    const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

    const cachedDataStr = localStorage.getItem(CACHE_KEY);
    if (cachedDataStr) {
      try {
        const cachedData = JSON.parse(cachedDataStr);
        if (Date.now() - cachedData.timestamp < CACHE_TTL) {
          if (cachedData.region) {
            setDetectedRegion(cachedData.region);
            setDetectedCity(cachedData.city);
            setStatus("detected");
          } else {
            setStatus("outside");
          }
          return;
        }
      } catch (e) {
        // ignore JSON parse error
      }
    }

    setStatus("detecting");

    // Use ipapi.co — free, no key required, returns JSON with lat/lon/city
    fetch("https://ipapi.co/json/")
      .then((res) => {
        if (!res.ok) throw new Error("Rate limited or error");
        return res.json();
      })
      .then((data) => {
        const lat = data.latitude as number;
        const lng = data.longitude as number;
        const city = (data.city as string) || null;

        if (!lat || !lng) {
          localStorage.setItem(CACHE_KEY, JSON.stringify({ timestamp: Date.now(), region: null, city: null }));
          setStatus("outside");
          return;
        }

        const region = findNearestRegion(lat, lng);
        localStorage.setItem(CACHE_KEY, JSON.stringify({ timestamp: Date.now(), region, city }));
        
        if (region) {
          setDetectedRegion(region);
          setDetectedCity(city);
          setStatus("detected");
        } else {
          setStatus("outside");
        }
      })
      .catch(() => {
        // Graceful fallback
        setStatus("outside");
      });
  }, []);

  const detectedRegionName = detectedRegion ? REGIONS[detectedRegion as keyof typeof REGIONS]?.name ?? null : null;

  return { status, detectedRegion, detectedRegionName, detectedCity, dismiss };
}
